<template>
    <div>
        <div class="module">
            <header class="module__header">
                <h1 data-test-id="page-title">Overview</h1>
            </header>

            <section class="module__body">
                <div class="overview">
                    <div
                        class="overview__stat"
                        data-test-id="frequency-sets-card"
                    >
                        <a
                            href="#"
                            @click.prevent="statClicked('admin-frequency-sets')"
                        >
                            <span
                                v-if="counts.frequency_set !== null"
                                class="count"
                                >{{ counts.frequency_set }}</span
                            >
                            <span
                                v-else
                                class="loader large"
                            ></span>

                            <span class="label">Frequency Sets</span>
                        </a>
                    </div>

                    <div
                        class="overview__stat"
                        data-test-id="users-card"
                    >
                        <a
                            href="#"
                            @click.prevent="statClicked('admin-users')"
                        >
                            <span
                                v-if="counts.user !== null"
                                class="count"
                                >{{ counts.user }}</span
                            >
                            <span
                                v-else
                                class="loader large"
                            ></span>

                            <span class="label">Users</span>
                        </a>
                    </div>

                    <div
                        class="overview__stat"
                        data-test-id="service-users-card"
                    >
                        <a
                            href="#"
                            @click.prevent="
                                statClicked('admin-users', null, { search_term: 'withMobileFeatures:Service User' })
                            "
                        >
                            <span
                                v-if="counts.user !== null"
                                class="count"
                                >{{ counts.service_user }}</span
                            >
                            <span
                                v-else
                                class="loader large"
                            ></span>

                            <span class="label">Service Users</span>
                        </a>
                    </div>

                    <div
                        class="overview__stat"
                        data-test-id="mobile-deploy-users-card"
                    >
                        <a
                            href="#"
                            @click.prevent="
                                statClicked('admin-users', null, {
                                    search_term: 'withMobileFeatures:Mobile Deployment User',
                                })
                            "
                        >
                            <span
                                v-if="counts.user !== null"
                                class="count"
                                >{{ counts.mobile_deployment_user }}</span
                            >
                            <span
                                v-else
                                class="loader large"
                            ></span>

                            <span class="label">Mobile Deployment Users </span>
                        </a>
                    </div>
                    <div
                        v-if="userCanSeeLicenseStats && purchasedCount !== null && purchasedCount > 0"
                        class="overview__stat not-linked"
                        data-test-id="licenses-card"
                        :class="{
                            disabled: purchasedCount === 0 && usedCount === 0,
                        }"
                    >
                        <a
                            href="#"
                            data-test-id="licenses-card__link"
                            :class="{
                                within_limit: usedCount < purchasedCount && purchasedCount > 0,
                                exceeds_limit: usedCount >= purchasedCount && usedCount > 0,
                            }"
                        >
                            <span
                                v-if="usedCount !== null"
                                class="count"
                                ><span data-test-id="licenses-card__link__used-count">{{ usedCount }}</span>
                                /
                                <span data-test-id="licenses-card__link__purchased-count">{{
                                    purchasedCount
                                }}</span></span
                            >
                            <span
                                v-else
                                class="loader large"
                            ></span>

                            <span class="label">License Usage</span>
                        </a>
                    </div>
                </div>
            </section>
        </div>

        <div class="two-module-wrapper">
            <div class="module software-details">
                <header class="module__header">
                    <h1>Latest Software Versions</h1>
                </header>

                <section class="module__body">
                    <ul>
                        <li>
                            <img
                                src="/images/pro-device-icon.png"
                                class="software-details__icon"
                            />
                            <p>
                                <span v-if="versions.pro !== null">{{ versions.pro }}</span>
                                <span
                                    v-else
                                    class="loader"
                                ></span>
                                <span>Pro Device Firmware</span>
                            </p>
                        </li>
                        <li>
                            <img
                                src="/images/gokit-icon.png"
                                class="software-details__icon"
                            />
                            <p>
                                <span v-if="versions.gokit !== null">{{ versions.gokit }}</span>
                                <span
                                    v-else
                                    class="loader"
                                ></span>
                                <span>goKit Software</span>
                            </p>
                        </li>
                    </ul>
                </section>
            </div>

            <div class="module">
                <header class="module__header">
                    <h1>Actions</h1>
                </header>

                <section class="module__body">
                    <span
                        v-if="actions === null"
                        class="loader large"
                    ></span>
                    <div v-else-if="actions.length">
                        <ul class="actions">
                            <li
                                v-for="(action, index) in actions"
                                :key="index"
                            >
                                <span v-if="action.model_count > 1">
                                    <span class="actions__type"
                                        >{{ action.model_count }} {{ pluralize(lowerCase(action.model_name)) }}</span
                                    >
                                </span>
                                <span v-else>
                                    <span class="actions__type">{{ action.model_name }}</span>
                                    {{ action.attribute }}
                                </span>
                                <span :class="`actions__action ${action.action}`">{{ action.action }}</span>
                                <span class="actions__when">{{ $filters.whenFromNow(action.created_at) }}</span>
                                <span
                                    v-if="action.user && action.user.support"
                                    class="actions__support"
                                    >by goTenna Support</span
                                >
                            </li>
                            <li v-if="actions.length">
                                <router-link
                                    tag="a"
                                    :to="{ name: 'admin-actions' }"
                                    >See All
                                </router-link>
                            </li>
                        </ul>
                    </div>
                    <p
                        v-else
                        class="no-results"
                    >
                        No actions taken.
                    </p>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import stringFormatting from '@/mixins/stringFormatting'
import canToast from '@/mixins/canToast'
import needsOrganizationSlug from '@/mixins/needsOrganizationSlug'
import Permissions from '@/mixins/permissions'

export default {
    mixins: [stringFormatting, canToast, needsOrganizationSlug, Permissions],

    data() {
        return {
            counts: {
                frequency_set: null,
                user: null,
                mobile_deployment_user: null,
            },
            versions: {
                pro: null,
                gokit: null,
            },
            actions: null,
            features: [],
        }
    },

    computed: {
        ...mapGetters('licenseStats', ['purchasedCount', 'usedCount']),
        ...mapGetters('licenseStats', { userCanSeeLicenseStats: 'userHasPermission' }),
    },

    mounted() {
        setTimeout(() => {
            this.$http.get(`${this.organizationSlug}/dashboard`).then(({ data }) => {
                if (data.organization) {
                    this.counts = data.organization.counts
                    this.features = data.organization.features
                }
                this.versions = data.software_versions
                this.actions = data.actions
            })
        }, 0)
    },

    methods: {
        statClicked(toRouteName, allowName, queryObject) {
            if ((allowName && this.organizationFeatureEnabled(allowName)) || !allowName) {
                this.$router.push({ name: toRouteName, query: queryObject })
            } else {
                this.toast(
                    'This feature is not enabled for this organization. Please contact support@gotenna.com to activate it.',
                    'error'
                )
            }
        },
        organizationFeatureEnabled(featureName) {
            return this.features.some((f) => f.feature === featureName)
        },
    },
}
</script>

<style scoped lang="scss">
.overview {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -10px;
}

.overview__stat {
    width: 25%;
    padding: 0 10px;
}

.overview__stat:nth-child(1n + 5) {
    margin-top: 15px;
}

.overview__stat > a {
    display: block;
    padding: 20px;
    text-align: center;
    border: 1px solid #636a6d;
    text-decoration: none;

    &.within_limit {
        border-color: #59c966;
    }

    &.exceeds_limit {
        border-color: #f96a62;
    }
}

.overview__stat:not(.disabled) {
    cursor: pointer;
}

.overview__stat.not-linked > a {
    cursor: default;
}

.overview__stat.disabled > a {
    color: #636a6d;
    cursor: not-allowed;
}

.overview__stat:not(.disabled) {
    > a:hover {
        background-color: #636a6d;
    }

    > a:hover .loader {
        border-color: #636a6d;
        border-top: 2px solid #fff;
    }
}

.overview__stat .count {
    animation: none;
}

.overview__stat .count,
.overview__stat .label {
    display: block;
}

.overview__stat .count,
.overview__stat .loader {
    margin-bottom: 15px;
    font-size: 2.5rem;
    font-family: 'Avenir Heavy';
}

.overview__stat .count {
    font-size: 2rem;
}

.overview__stat .label {
    font-family: 'Avenir Book';
    font-size: 0.875rem;
    line-height: 1.5;
    text-transform: uppercase;
    font-size: 0.6875rem;
    letter-spacing: 1px;
}

.software-details p {
    display: inline-block;
    position: relative;
    top: -6px;
    font-family: 'Avenir Light';
}

.software-details p > span {
    display: block;

    &:not(.loader) {
        animation: none;
    }
}

.software-details p > span:first-child {
    font-family: 'Avenir Book';
}

.software-details p > span:last-child {
    font-size: 0.875rem;
    color: #999;
}

.software-details ul {
    display: flex;
}

.software-details li {
    display: block;
    width: 50%;
}

.software-details__icon {
    width: 45px;
    margin-right: 8px;
    background-color: #555c5f;
    border-radius: 8px;
    padding: 5px;
}

.actions span {
    margin-right: 4px;
}

.actions li {
    font-family: 'Avenir Book';
    font-size: 0.875rem;
}

.actions li:not(:last-child) {
    margin-bottom: 20px;
}

.actions__when,
.actions__support {
    font-size: 0.6875rem;
    color: #999;
}

.actions__action.deleted {
    color: #ff7871;
}

.actions__action.updated {
    color: #ffb471;
}

.actions__action.imported,
.actions__action.created,
.actions__action.added {
    color: #59c966;
}
</style>
